import { useState, useRef, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDocuments } from '../../hooks/useApi';
import type { Document } from '../../api';

interface DocumentViewerProps {
  onSelectDocument: (id: string) => void;
  selectedId: string | null;
  filters: Record<string, any>;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SOURCE_COLORS: Record<string, { bg: string; text: string }> = {
  EFTA:           { bg: 'rgba(88,101,242,0.15)', text: '#8b9aff' },
  'Epstein Docs': { bg: 'rgba(59,165,92,0.15)',  text: '#5ec97a' },
  'jmail.world':  { bg: 'rgba(250,166,26,0.15)', text: '#ffc94d' },
  DOJ:            { bg: 'rgba(237,66,69,0.15)',   text: '#ff7b7d' },
};

const SOURCE_DOT_COLORS: Record<string, string> = {
  EFTA:           '#5865F2',
  'Epstein Docs': '#3BA55C',
  'jmail.world':  '#FAA61A',
  DOJ:            '#ED4245',
};

/** Compact labels for source badges */
const SOURCE_LABELS: Record<string, string> = {
  EFTA:           'EFTA',
  'Epstein Docs': 'Docs',
  'jmail.world':  'JMail',
  DOJ:            'DOJ',
  'Doj-March2026':'DOJ-26',
};

const CATEGORY_COLORS: Record<string, string> = {
  email:          '#5865F2',
  memo:           '#9b59b6',
  contract:       '#e67e22',
  legal:          '#ED4245',
  financial:      '#FAA61A',
  photograph:     '#1abc9c',
  report:         '#3498db',
  correspondence: '#3BA55C',
  court:          '#e74c3c',
  deposition:     '#a855f7',
  testimony:      '#06b6d4',
  filing:         '#f97316',
};

const CATEGORIES = [
  'email', 'memo', 'contract', 'legal', 'financial',
  'photograph', 'report', 'correspondence', 'court',
  'deposition', 'testimony', 'filing',
];

const SOURCES = ['EFTA', 'Epstein Docs', 'jmail.world', 'DOJ'];

const SORT_OPTIONS = [
  { value: 'newest',  label: 'Newest' },
  { value: 'oldest',  label: 'Oldest' },
  { value: 'pages',   label: 'Pages' },
  { value: 'persons', label: 'Persons' },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Format a raw date string to a readable short form */
function formatDate(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
}

/** Format large page counts compactly: 1826 -> "1.8K" */
function formatPages(n: number): string {
  if (n >= 10000) return `${(n / 1000).toFixed(0)}K`;
  if (n >= 1000)  return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

/** Format doc count: 2146580 -> "2,146,580" */
function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** Truncate text with ellipsis */
function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '\u2026';
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

/** Search input with an integrated clear (x) button */
function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="dv-search-wrap">
      <svg className="dv-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="text"
        className="dv-search"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          className="dv-search-clear"
          onClick={() => onChange('')}
          aria-label="Clear search"
          title="Clear search"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

/** Styled select dropdown */
function FilterSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="dv-select-wrap">
      <select
        className="dv-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </select>
      <svg className="dv-select-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
}

/** Compact source badge with consistent width */
function SourceBadge({ source }: { source: string }) {
  const colors = SOURCE_COLORS[source] || { bg: 'rgba(150,150,170,0.12)', text: '#8888aa' };
  const label = SOURCE_LABELS[source] || source.slice(0, 6);

  return (
    <span
      className="dv-source-badge"
      style={{ background: colors.bg, color: colors.text }}
    >
      {label}
    </span>
  );
}

/** Small page-count pill */
function PagePill({ count }: { count: number }) {
  if (!count || count <= 0) return null;
  return (
    <span className="dv-page-pill">
      {formatPages(count)}p
    </span>
  );
}

/** Category indicator as a colored left border accent */
function CategoryDot({ category }: { category: string }) {
  const color = CATEGORY_COLORS[category] || '#606070';
  return (
    <span
      className="dv-cat-dot"
      style={{ backgroundColor: color }}
      title={category}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Document Row                                                       */
/* ------------------------------------------------------------------ */

function DocumentRow({
  doc,
  isSelected,
  onClick,
  style,
}: {
  doc: Document;
  isSelected: boolean;
  onClick: () => void;
  style: React.CSSProperties;
}) {
  const categoryColor = CATEGORY_COLORS[doc.category] || '#606070';

  return (
    <button
      className={`dv-row${isSelected ? ' dv-row--selected' : ''}`}
      style={{
        ...style,
        borderLeftColor: isSelected ? 'var(--accent)' : categoryColor,
      }}
      onClick={onClick}
      title={doc.title}
    >
      {/* Title line */}
      <div className="dv-row-header">
        <span className="dv-row-title">{doc.title}</span>
        <span className="dv-row-date">{formatDate(doc.date)}</span>
      </div>

      {/* Summary line */}
      {doc.summary && (
        <div className="dv-row-summary">
          {truncate(doc.summary, 160)}
        </div>
      )}

      {/* Metadata line */}
      <div className="dv-row-meta">
        <SourceBadge source={doc.source} />
        {doc.category && (
          <span className="dv-row-category">{doc.category}</span>
        )}
        <PagePill count={doc.page_count} />
        {doc.person_count > 0 && (
          <span className="dv-person-count">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            {doc.person_count}
          </span>
        )}
        {doc.bates_range && (
          <span className="dv-bates">{doc.bates_range}</span>
        )}
        {doc.pdf_url && (
          <span className="dv-has-pdf" title="PDF available">PDF</span>
        )}
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Pagination                                                         */
/* ------------------------------------------------------------------ */

function Pagination({
  page,
  hasMore,
  total,
  perPage,
  onPageChange,
}: {
  page: number;
  hasMore: boolean;
  total: number;
  perPage: number;
  onPageChange: (p: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const rangeStart = (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  return (
    <div className="dv-pagination">
      <span className="dv-pagination-range">
        {formatCount(rangeStart)}&ndash;{formatCount(rangeEnd)}
      </span>

      <div className="dv-pagination-controls">
        <button
          className="dv-pagination-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
          title="First page"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="11 17 6 12 11 7" />
            <polyline points="18 17 13 12 18 7" />
          </svg>
        </button>
        <button
          className="dv-pagination-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          title="Previous page"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <span className="dv-pagination-page">
          {page} <span className="dv-pagination-of">/ {totalPages}</span>
        </span>

        <button
          className="dv-pagination-btn"
          disabled={!hasMore}
          onClick={() => onPageChange(page + 1)}
          title="Next page"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <button
          className="dv-pagination-btn"
          disabled={!hasMore}
          onClick={() => onPageChange(totalPages)}
          title="Last page"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13 17 18 12 13 7" />
            <polyline points="6 17 11 12 6 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Loading skeleton                                                   */
/* ------------------------------------------------------------------ */

function LoadingSkeleton() {
  return (
    <div className="dv-skeleton-list">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="dv-skeleton-row">
          <div className="dv-skeleton-line dv-skeleton-title" />
          <div className="dv-skeleton-line dv-skeleton-summary" />
          <div className="dv-skeleton-badges">
            <div className="dv-skeleton-line dv-skeleton-badge" />
            <div className="dv-skeleton-line dv-skeleton-badge" />
            <div className="dv-skeleton-line dv-skeleton-badge-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Active filter chips                                                */
/* ------------------------------------------------------------------ */

function ActiveFilters({
  category,
  source,
  search,
  onClearCategory,
  onClearSource,
  onClearSearch,
  onClearAll,
}: {
  category: string;
  source: string;
  search: string;
  onClearCategory: () => void;
  onClearSource: () => void;
  onClearSearch: () => void;
  onClearAll: () => void;
}) {
  const hasAny = category || source || search;
  if (!hasAny) return null;

  return (
    <div className="dv-active-filters">
      {search && (
        <span className="dv-filter-chip">
          &ldquo;{truncate(search, 20)}&rdquo;
          <button onClick={onClearSearch} className="dv-chip-x">&times;</button>
        </span>
      )}
      {category && (
        <span className="dv-filter-chip">
          {category}
          <button onClick={onClearCategory} className="dv-chip-x">&times;</button>
        </span>
      )}
      {source && (
        <span className="dv-filter-chip">
          {source}
          <button onClick={onClearSource} className="dv-chip-x">&times;</button>
        </span>
      )}
      <button className="dv-clear-all" onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function DocumentViewer({ onSelectDocument, selectedId, filters }: DocumentViewerProps) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const parentRef = useRef<HTMLDivElement>(null);

  // Reset page when filters change
  const updateSearch = useCallback((v: string) => { setSearch(v); setPage(1); }, []);
  const updateCategory = useCallback((v: string) => { setCategory(v); setPage(1); }, []);
  const updateSource = useCallback((v: string) => { setSource(v); setPage(1); }, []);
  const updateSort = useCallback((v: string) => { setSort(v); setPage(1); }, []);

  const { data, isLoading, isFetching } = useDocuments({
    q: search || undefined,
    category: category || undefined,
    source: source || undefined,
    sort,
    page,
    per_page: 100,
    date_from: filters.date_from,
    date_to: filters.date_to,
  });

  const docs = data?.data || [];
  const meta = data?.meta;

  const virtualizer = useVirtualizer({
    count: docs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 88,
    overscan: 12,
  });

  // Scroll to top on page change
  const handlePageChange = useCallback((p: number) => {
    setPage(p);
    parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="dv-container">
      {/* ---------- Toolbar ---------- */}
      <div className="dv-toolbar">
        <SearchInput
          value={search}
          onChange={updateSearch}
          placeholder="Search documents..."
        />
        <div className="dv-toolbar-filters">
          <FilterSelect value={category} onChange={updateCategory}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </FilterSelect>

          <FilterSelect value={source} onChange={updateSource}>
            <option value="">All sources</option>
            {SOURCES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </FilterSelect>

          <FilterSelect value={sort} onChange={updateSort}>
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </FilterSelect>
        </div>
      </div>

      {/* ---------- Active filter chips ---------- */}
      <ActiveFilters
        category={category}
        source={source}
        search={search}
        onClearCategory={() => updateCategory('')}
        onClearSource={() => updateSource('')}
        onClearSearch={() => updateSearch('')}
        onClearAll={() => {
          updateSearch('');
          updateCategory('');
          updateSource('');
        }}
      />

      {/* ---------- Status bar ---------- */}
      {meta && (
        <div className="dv-status-bar">
          <div className="dv-status-left">
            <span className="dv-doc-count">
              {formatCount(meta.total)}
            </span>
            <span className="dv-doc-label">documents</span>
            {isFetching && <span className="dv-loading-dot" />}
          </div>
          <Pagination
            page={meta.page}
            hasMore={meta.has_more}
            total={meta.total}
            perPage={meta.per_page}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* ---------- Document list ---------- */}
      <div className="dv-list" ref={parentRef}>
        {isLoading ? (
          <LoadingSkeleton />
        ) : docs.length === 0 ? (
          <div className="dv-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="9" y1="15" x2="15" y2="15" />
            </svg>
            <span className="dv-empty-text">No documents found</span>
            {(search || category || source) && (
              <button
                className="dv-empty-clear"
                onClick={() => { updateSearch(''); updateCategory(''); updateSource(''); }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((vItem) => {
              const doc = docs[vItem.index];
              return (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  isSelected={selectedId === doc.id}
                  onClick={() => onSelectDocument(doc.id)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${vItem.size}px`,
                    transform: `translateY(${vItem.start}px)`,
                  }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ---------- Bottom pagination (if many results) ---------- */}
      {meta && meta.total > 50 && (
        <div className="dv-bottom-bar">
          <Pagination
            page={meta.page}
            hasMore={meta.has_more}
            total={meta.total}
            perPage={meta.per_page}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
